import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-2xl font-bold text-white">{title}</h2>
    <div className="mt-3 space-y-3 text-neutral-300 leading-relaxed">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Tietosuojaseloste | Ysitien LED";
    window.scrollTo(0, 0);
  }, []);
  const updated = "3.7.2026";
  return (
    <div className="bg-[#07111F] text-white min-h-screen">
      <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16 md:py-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#22D3EE] hover:text-[#67E8F9] transition-colors"
          data-testid="privacy-back"
        >
          <ArrowLeft className="h-4 w-4" /> Takaisin etusivulle
        </Link>

        <h1 className="mt-8 font-display text-4xl md:text-5xl font-bold leading-tight">
          Tietosuojaseloste
        </h1>
        <p className="mt-3 text-sm text-neutral-500">
          Päivitetty {updated} · EU:n yleisen tietosuoja-asetuksen (GDPR 2016/679) mukainen
          rekisteri- ja tietosuojaseloste.
        </p>

        <Section title="1. Rekisterinpitäjä">
          <p>
            Ysitien LED (jäljempänä ”me” tai ”palvelu”). Yhteystiedot:
          </p>
          <ul className="mt-2 space-y-1">
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-[#22D3EE]" /> juho@myyntidrive.fi
            </li>
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-[#22D3EE]" /> +358 50 553 1110
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#22D3EE]" /> Loimaa, Suomi
            </li>
          </ul>
        </Section>

        <Section title="2. Rekisterin nimi">
          <p>Ysitien LED -verkkosivuston yhteydenotto- ja markkinointirekisteri.</p>
        </Section>

        <Section title="3. Henkilötietojen käsittelyn tarkoitus ja peruste">
          <p>
            Käsittelemme henkilötietoja seuraaviin tarkoituksiin:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Yhteydenottopyyntöihin vastaaminen</li>
            <li>LED-mainospaikkojen tarjousten ja tilausten hoitaminen</li>
            <li>Asiakassuhteen ylläpito ja hoito</li>
            <li>Palveluun liittyvä viestintä (esim. saatavuustilanne, kampanjat)</li>
          </ul>
          <p>
            Käsittelyn oikeusperusteena on rekisteröidyn suostumus (lomakkeella
            annettu tieto), sopimuksen valmistelu tai täytäntöönpano ja
            rekisterinpitäjän oikeutettu etu asiakassuhteen hoitamiseksi.
          </p>
        </Section>

        <Section title="4. Rekisterin tietosisältö">
          <p>Rekisteri voi sisältää seuraavat tiedot:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Nimi ja yrityksen nimi</li>
            <li>Sähköpostiosoite ja puhelinnumero</li>
            <li>Yhteydenottoon liittyvä viesti sekä kiinnostava mainosjakso</li>
            <li>Yhteydenoton ajankohta ja lähde (verkkolomake / sähköpostipopup)</li>
          </ul>
        </Section>

        <Section title="5. Säännönmukaiset tietolähteet">
          <p>
            Rekisterin tiedot kerätään rekisteröidyltä itseltään verkkosivuston
            yhteydenotto- ja sähköpostilomakkeiden kautta.
          </p>
        </Section>

        <Section title="6. Tietojen luovuttaminen ja siirto">
          <p>
            Emme luovuta henkilötietoja ulkopuolisille tahoille markkinointi-
            tarkoituksiin. Tietoja voidaan luovuttaa viranomaisille laissa
            säädetyissä tilanteissa. Tietoja käsitellään Euroopan talousalueella
            (ETA) sijaitsevilla palvelimilla.
          </p>
        </Section>

        <Section title="7. Rekisterin suojaus">
          <p>
            Tiedot säilytetään pääsyoikeuksin suojatussa tietokannassa.
            Käsittely on rajoitettu vain niille henkilöille, joilla on
            tehtäviensä puolesta perusteltu tarve käsitellä tietoja.
          </p>
        </Section>

        <Section title="8. Säilytysaika">
          <p>
            Yhteystietoja säilytetään niin kauan kuin on tarpeen asiakassuhteen
            hoitamiseksi ja tarjouspyyntöihin vastaamiseksi, kuitenkin enintään
            36 kuukautta viimeisestä yhteydenotosta, ellei laki edellytä
            pidempää säilytystä.
          </p>
        </Section>

        <Section title="9. Rekisteröidyn oikeudet">
          <p>Rekisteröidyllä on oikeus:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>tarkastaa itseään koskevat tallennetut tiedot,</li>
            <li>pyytää virheellisten tietojen oikaisua,</li>
            <li>pyytää tietojensa poistamista (”oikeus tulla unohdetuksi”),</li>
            <li>peruuttaa antamansa suostumus koska tahansa,</li>
            <li>tehdä valitus valvontaviranomaiselle (Tietosuojavaltuutetun toimisto).</li>
          </ul>
          <p>
            Pyynnöt osoitetaan sähköpostitse osoitteeseen{" "}
            <a href="mailto:juho@myyntidrive.fi" className="text-[#22D3EE] hover:underline">
              juho@myyntidrive.fi
            </a>
            .
          </p>
        </Section>

        <Section title="10. Evästeet">
          <p>
            Sivusto käyttää ainoastaan sivuston toiminnan kannalta välttämättömiä
            paikallistallennus- ja evästetyyppisiä tekniikoita (esim. pop-up-
            ikkunan ja evästeilmoituksen tilan muistamiseen). Emme käytä
            kolmansien osapuolten analytiikka- tai markkinointievästeitä ilman
            erillistä suostumusta.
          </p>
        </Section>

        <Section title="11. Muutokset tietosuojaselosteeseen">
          <p>
            Pidätämme oikeuden päivittää tätä selostetta. Viimeisin
            päivityspäivä on merkitty sivun yläosaan.
          </p>
        </Section>

        <div className="mt-16 border-t border-[#1E2F44] pt-6 text-sm text-neutral-500">
          © {new Date().getFullYear()} Ysitien LED
        </div>
      </div>
    </div>
  );
}
